import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { ClienteNote } from '../types/crm';
import { logger } from '../utils/logger';

interface ClienteNotesState {
  notes: Record<string, ClienteNote[]>; // Keyed by cliente_id
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchNotes: (clienteId: string, empresaId: string) => Promise<void>;
  createNote: (note: Omit<ClienteNote, 'id' | 'created_at'>, empresaId: string) => Promise<ClienteNote | null>;
  updateNote: (id: string, updates: Partial<ClienteNote>, empresaId: string) => Promise<ClienteNote | null>;
  deleteNote: (id: string, empresaId: string) => Promise<boolean>;
  clearError: () => void;
}

export const useClienteNotesStore = create<ClienteNotesState>((set, _get) => ({
  notes: {},
  isLoading: false,
  error: null,

  fetchNotes: async (clienteId: string, empresaId: string) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('cliente_notes')
        .select(`
          *,
          usuarios:user_id (
            id,
            nombre,
            email
          )
        `)
        .eq('cliente_id', clienteId)
        .eq('empresa_id', empresaId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform data to include usuario info
      const transformedNotes = (data || []).map((note: any) => ({
        ...note,
        usuario_nombre: note.usuarios?.nombre || 'Usuario desconocido',
        usuario_email: note.usuarios?.email || '',
      }));

      set(state => ({
        notes: {
          ...state.notes,
          [clienteId]: transformedNotes,
        },
        isLoading: false,
      }));
    } catch (error: any) {
      logger.error('Error fetching cliente notes:', error);
      set({ error: error.message, isLoading: false });
    }
  },

  createNote: async (note, empresaId) => {
    try {
      const { data, error } = await supabase
        .from('cliente_notes')
        .insert({
          ...note,
          empresa_id: empresaId,
          created_at: new Date().toISOString(),
        })
        .select(`
          *,
          usuarios:user_id (
            id,
            nombre,
            email
          )
        `)
        .single();

      if (error) throw error;

      const transformedNote = {
        ...data,
        usuario_nombre: data.usuarios?.nombre || 'Usuario desconocido',
        usuario_email: data.usuarios?.email || '',
      };

      set(state => ({
        notes: {
          ...state.notes,
          [note.cliente_id]: [
            transformedNote,
            ...(state.notes[note.cliente_id] || []),
          ],
        },
      }));

      return transformedNote;
    } catch (error: any) {
      logger.error('Error creating cliente note:', error);
      set({ error: error.message });
      return null;
    }
  },

  updateNote: async (id, updates, empresaId) => {
    try {
      const { data, error } = await supabase
        .from('cliente_notes')
        .update(updates)
        .eq('id', id)
        .eq('empresa_id', empresaId)
        .select(`
          *,
          usuarios:user_id (
            id,
            nombre,
            email
          )
        `)
        .single();

      if (error) throw error;

      const transformedNote = {
        ...data,
        usuario_nombre: data.usuarios?.nombre || 'Usuario desconocido',
        usuario_email: data.usuarios?.email || '',
      };

      // Find which cliente this note belongs to
      const clienteId = data.cliente_id;

      set(state => ({
        notes: {
          ...state.notes,
          [clienteId]: (state.notes[clienteId] || []).map(n =>
            n.id === id ? transformedNote : n
          ),
        },
      }));

      return transformedNote;
    } catch (error: any) {
      logger.error('Error updating cliente note:', error);
      set({ error: error.message });
      return null;
    }
  },

  deleteNote: async (id, empresaId) => {
    try {
      // First get the note to find cliente_id
      const { data: note, error: fetchError } = await supabase
        .from('cliente_notes')
        .select('cliente_id')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('cliente_notes')
        .delete()
        .eq('id', id)
        .eq('empresa_id', empresaId);

      if (error) throw error;

      if (note) {
        set(state => ({
          notes: {
            ...state.notes,
            [note.cliente_id]: (state.notes[note.cliente_id] || []).filter(n => n.id !== id),
          },
        }));
      }

      return true;
    } catch (error: any) {
      logger.error('Error deleting cliente note:', error);
      set({ error: error.message });
      return false;
    }
  },

  clearError: () => set({ error: null }),
}));


